/**
 * BOT PREMIUM Tabs - JavaScript
 * Tab switching, transitions, slider (arrows/dots/drag/keyboard/pause), Theme Editor
 */

(function() {
  'use strict';

  var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function initPremiumTabs(section) {
    if (!section) return;

    var contentMode = section.dataset.contentMode || 'grid';
    var transition = section.dataset.transition || 'fade';
    var tabModeDesktop = section.dataset.tabModeDesktop || 'horizontal';
    var tabModeMobile = section.dataset.tabModeMobile || 'dropdown';
    var dotsStyle = section.dataset.dotsStyle || 'circles';

    // Desktop tabs
    var desktopTabs = section.querySelectorAll('.bot-ptabs__nav .bot-ptabs__tab');
    // Mobile tabs (scroll or stacked)
    var mobileTabs = section.querySelectorAll('.bot-ptabs__tab-mobile, .bot-ptabs__tab-stacked');
    // Custom Dropdown
    var dropdownWrap = section.querySelector('[data-tab-dropdown-wrap]');
    var dropdownToggle = section.querySelector('[data-tab-dropdown-toggle]');
    var dropdownList = section.querySelector('[data-tab-dropdown-list]');
    var dropdownItems = section.querySelectorAll('.bot-ptabs__dropdown-item');
    // Panels
    var panels = section.querySelectorAll('.bot-ptabs__panel');
    // Underline indicator
    var underlineIndicator = section.querySelector('.bot-ptabs__underline-indicator');

    if (!panels.length) return;

    var currentIndex = 0;
    var sliders = {}; // per-panel slider state

    // ========================================
    // Tab Switching
    // ========================================

    function switchTab(index) {
      if (index < 0 || index >= panels.length) return;
      currentIndex = index;

      // Update desktop tabs
      desktopTabs.forEach(function(tab) {
        var isActive = parseInt(tab.dataset.tabIndex, 10) === index;
        tab.classList.toggle('is-active', isActive);
        tab.setAttribute('aria-selected', String(isActive));
        tab.tabIndex = isActive ? 0 : -1;
      });

      // Update mobile tabs
      mobileTabs.forEach(function(tab) {
        var isActive = parseInt(tab.dataset.tabIndex, 10) === index;
        tab.classList.toggle('is-active', isActive);
      });

      // Sync custom dropdown
      if (dropdownWrap) {
        var activeItem = dropdownWrap.querySelector('.bot-ptabs__dropdown-item[data-tab-index="' + index + '"]');
        if (activeItem) {
          // Update toggle content
          var toggleIcon = dropdownToggle.querySelector('.bot-ptabs__tab-icon');
          var toggleEmoji = dropdownToggle.querySelector('.bot-ptabs__tab-emoji');
          var toggleLabel = dropdownToggle.querySelector('[data-dropdown-label]');
          var itemIcon = activeItem.querySelector('.bot-ptabs__tab-icon');
          var itemEmoji = activeItem.querySelector('.bot-ptabs__tab-emoji');
          var itemLabel = activeItem.querySelector('span:last-child');

          // Replace icon
          if (toggleIcon) toggleIcon.remove();
          if (toggleEmoji) toggleEmoji.remove();
          if (itemIcon && toggleLabel) toggleLabel.before(itemIcon.cloneNode(true));
          if (itemEmoji && toggleLabel) toggleLabel.before(itemEmoji.cloneNode(true));

          // Replace label
          if (toggleLabel && itemLabel) toggleLabel.textContent = itemLabel.textContent;

          // Update active state
          dropdownItems.forEach(function(item) {
            item.classList.toggle('is-active', parseInt(item.dataset.tabIndex, 10) === index);
          });
        }
        // Close dropdown
        dropdownWrap.classList.remove('is-open');
      }

      // Update panels
      panels.forEach(function(panel) {
        var isActive = parseInt(panel.dataset.panel, 10) === index;
        panel.classList.toggle('is-active', isActive);
        panel.hidden = !isActive;
      });

      // Update underline indicator
      if (underlineIndicator && desktopTabs.length > 0) {
        updateUnderline(index);
      }

      // Initialize slider for active panel if needed
      if (contentMode === 'slider') {
        initSliderForPanel(index);
      }
    }

    // ========================================
    // Underline Indicator
    // ========================================

    function updateUnderline(index) {
      if (!underlineIndicator) return;
      var activeTab = desktopTabs[index];
      if (!activeTab) return;

      var nav = activeTab.parentElement;
      var navRect = nav.getBoundingClientRect();
      var tabRect = activeTab.getBoundingClientRect();

      underlineIndicator.style.width = tabRect.width + 'px';
      underlineIndicator.style.left = (tabRect.left - navRect.left + nav.scrollLeft) + 'px';

      // Auto-scroll para que el tab activo sea visible
      var tabLeft = tabRect.left - navRect.left + nav.scrollLeft;
      var tabRight = tabLeft + tabRect.width;
      if (tabLeft < nav.scrollLeft) {
        nav.scrollTo({ left: tabLeft - 20, behavior: 'smooth' });
      } else if (tabRight > nav.scrollLeft + nav.offsetWidth) {
        nav.scrollTo({ left: tabRight - nav.offsetWidth + 20, behavior: 'smooth' });
      }
    }

    // ========================================
    // Slider Functionality
    // ========================================

    function initSliderForPanel(panelIndex) {
      var panel = panels[panelIndex];
      if (!panel) return;

      var grid = panel.querySelector('[data-bot-slider]');
      if (!grid) return;

      // Already initialized
      if (sliders[panelIndex]) {
        sliders[panelIndex].update();
        return;
      }

      var track = grid.querySelector('.bot-ptabs__track');
      var items = track ? track.querySelectorAll('.bot-ptabs__item') : [];
      var prevBtn = grid.querySelector('[data-arrow="prev"]');
      var nextBtn = grid.querySelector('[data-arrow="next"]');
      var dotsContainer = grid.querySelector('[data-dots]');
      var autoplaySpeed = parseInt(grid.dataset.autoplay, 10) || 0;
      var loop = grid.dataset.loop === 'true';

      if (!track || items.length === 0) return;

      var state = {
        current: 0,
        total: items.length,
        perView: getPerView(),
        autoplayTimer: null
      };

      function getPerView() {
        var w = window.innerWidth;
        var style = getComputedStyle(section);
        if (w >= 1024) return parseInt(style.getPropertyValue('--bot-ptabs-columns-dk')) || 4;
        if (w >= 768) return parseInt(style.getPropertyValue('--bot-ptabs-columns-tb')) || 3;
        return parseInt(style.getPropertyValue('--bot-ptabs-columns-mb')) || 2;
      }

      function getMaxIndex() {
        return Math.max(0, state.total - state.perView);
      }

      function goTo(index) {
        if (loop) {
          if (index > getMaxIndex()) index = 0;
          if (index < 0) index = getMaxIndex();
        } else {
          index = Math.max(0, Math.min(index, getMaxIndex()));
        }

        state.current = index;

        var gap = parseInt(getComputedStyle(track).gap) || 20;
        var itemWidth = items[0].offsetWidth;
        var offset = index * (itemWidth + gap);
        track.style.transform = 'translateX(-' + offset + 'px)';

        updateArrows();
        updateDots();
      }

      function updateArrows() {
        if (!prevBtn || !nextBtn) return;
        if (loop) {
          prevBtn.disabled = false;
          nextBtn.disabled = false;
        } else {
          prevBtn.disabled = state.current <= 0;
          nextBtn.disabled = state.current >= getMaxIndex();
        }
      }

      function buildDots() {
        if (!dotsContainer) return;
        dotsContainer.innerHTML = '';
        var totalDots = getMaxIndex() + 1;
        for (var i = 0; i < totalDots; i++) {
          var dot = document.createElement('button');
          dot.className = 'bot-ptabs__dot' + (i === 0 ? ' is-active' : '');
          dot.setAttribute('aria-label', 'Ir a pagina ' + (i + 1));
          dot.dataset.dotIndex = i;
          // Numbers style: show number text
          if (dotsStyle === 'numbers') {
            dot.textContent = i + 1;
          }
          dot.addEventListener('click', function() {
            goTo(parseInt(this.dataset.dotIndex, 10));
            resetAutoplay();
          });
          dotsContainer.appendChild(dot);
        }
      }

      function updateDots() {
        if (!dotsContainer) return;
        var dots = dotsContainer.querySelectorAll('.bot-ptabs__dot');
        dots.forEach(function(dot) {
          dot.classList.toggle('is-active', parseInt(dot.dataset.dotIndex, 10) === state.current);
        });
      }

      function startAutoplay() {
        if (autoplaySpeed <= 0 || prefersReducedMotion) return;
        stopAutoplay();
        state.autoplayTimer = setInterval(function() {
          goTo(state.current + 1);
        }, autoplaySpeed);
      }

      function stopAutoplay() {
        if (state.autoplayTimer) {
          clearInterval(state.autoplayTimer);
          state.autoplayTimer = null;
        }
      }

      function resetAutoplay() {
        stopAutoplay();
        startAutoplay();
      }

      // Arrow click
      if (prevBtn) {
        prevBtn.addEventListener('click', function() {
          goTo(state.current - 1);
          resetAutoplay();
        });
      }
      if (nextBtn) {
        nextBtn.addEventListener('click', function() {
          goTo(state.current + 1);
          resetAutoplay();
        });
      }

      // Touch swipe
      var touchStartX = 0;
      var touchDiffX = 0;

      track.addEventListener('touchstart', function(e) {
        touchStartX = e.touches[0].clientX;
        touchDiffX = 0;
        stopAutoplay();
      }, { passive: true });

      track.addEventListener('touchmove', function(e) {
        touchDiffX = e.touches[0].clientX - touchStartX;
      }, { passive: true });

      track.addEventListener('touchend', function() {
        if (Math.abs(touchDiffX) > 50) {
          if (touchDiffX < 0) {
            goTo(state.current + 1);
          } else {
            goTo(state.current - 1);
          }
        }
        startAutoplay();
      });

      // Mouse drag on track
      var isDragging = false;
      var dragStartX = 0;
      var dragDiffX = 0;

      track.addEventListener('mousedown', function(e) {
        isDragging = true;
        dragStartX = e.pageX;
        dragDiffX = 0;
        track.style.cursor = 'grabbing';
        stopAutoplay();
        e.preventDefault();
      });

      document.addEventListener('mousemove', function(e) {
        if (!isDragging) return;
        dragDiffX = e.pageX - dragStartX;
      });

      document.addEventListener('mouseup', function() {
        if (!isDragging) return;
        isDragging = false;
        track.style.cursor = '';
        if (Math.abs(dragDiffX) > 50) {
          if (dragDiffX < 0) {
            goTo(state.current + 1);
          } else {
            goTo(state.current - 1);
          }
        }
        startAutoplay();
      });

      // Keyboard navigation (left/right arrows when slider focused)
      grid.setAttribute('tabindex', '0');
      grid.addEventListener('keydown', function(e) {
        if (e.key === 'ArrowRight') {
          e.preventDefault();
          goTo(state.current + 1);
          resetAutoplay();
        } else if (e.key === 'ArrowLeft') {
          e.preventDefault();
          goTo(state.current - 1);
          resetAutoplay();
        }
      });

      // Pause on hover
      grid.addEventListener('mouseenter', function() {
        stopAutoplay();
      });
      grid.addEventListener('mouseleave', function() {
        startAutoplay();
      });

      // Build and init
      buildDots();
      updateArrows();
      startAutoplay();

      sliders[panelIndex] = {
        update: function() {
          state.perView = getPerView();
          buildDots();
          goTo(Math.min(state.current, getMaxIndex()));
        },
        destroy: function() {
          stopAutoplay();
        }
      };
    }

    // ========================================
    // Desktop Tab Clicks
    // ========================================

    desktopTabs.forEach(function(tab) {
      tab.addEventListener('click', function() {
        switchTab(parseInt(tab.dataset.tabIndex, 10));
      });
    });

    // ========================================
    // Mobile Tab Clicks (scroll / stacked)
    // ========================================

    mobileTabs.forEach(function(tab) {
      tab.addEventListener('click', function() {
        switchTab(parseInt(tab.dataset.tabIndex, 10));
      });
    });

    // ========================================
    // Custom Dropdown
    // ========================================

    if (dropdownToggle) {
      dropdownToggle.addEventListener('click', function(e) {
        e.stopPropagation();
        dropdownWrap.classList.toggle('is-open');
      });

      // Close on click outside
      document.addEventListener('click', function(e) {
        if (dropdownWrap && !dropdownWrap.contains(e.target)) {
          dropdownWrap.classList.remove('is-open');
        }
      });
    }

    dropdownItems.forEach(function(item) {
      item.addEventListener('click', function(e) {
        e.stopPropagation();
        switchTab(parseInt(item.dataset.tabIndex, 10));
      });
    });

    // ========================================
    // Keyboard Navigation (tabs)
    // ========================================

    desktopTabs.forEach(function(tab) {
      tab.addEventListener('keydown', function(e) {
        var idx = parseInt(tab.dataset.tabIndex, 10);
        var newIndex = idx;
        var isVertical = tabModeDesktop === 'vertical';

        if ((!isVertical && e.key === 'ArrowRight') || (isVertical && e.key === 'ArrowDown')) {
          e.preventDefault();
          newIndex = (idx + 1) % desktopTabs.length;
        } else if ((!isVertical && e.key === 'ArrowLeft') || (isVertical && e.key === 'ArrowUp')) {
          e.preventDefault();
          newIndex = (idx - 1 + desktopTabs.length) % desktopTabs.length;
        } else if (e.key === 'Home') {
          e.preventDefault();
          newIndex = 0;
        } else if (e.key === 'End') {
          e.preventDefault();
          newIndex = desktopTabs.length - 1;
        } else {
          return;
        }

        switchTab(newIndex);
        desktopTabs[newIndex].focus();
      });
    });

    // ========================================
    // Scroll Mode - Drag scroll for desktop tabs
    // ========================================

    var scrollNav = section.querySelector('.bot-ptabs__nav--scroll');
    if (scrollNav) {
      var isDown = false;
      var startX = 0;
      var scrollLeft = 0;

      scrollNav.addEventListener('mousedown', function(e) {
        isDown = true;
        scrollNav.style.cursor = 'grabbing';
        startX = e.pageX - scrollNav.offsetLeft;
        scrollLeft = scrollNav.scrollLeft;
      });

      scrollNav.addEventListener('mouseleave', function() {
        isDown = false;
        scrollNav.style.cursor = 'grab';
      });

      scrollNav.addEventListener('mouseup', function() {
        isDown = false;
        scrollNav.style.cursor = 'grab';
      });

      scrollNav.addEventListener('mousemove', function(e) {
        if (!isDown) return;
        e.preventDefault();
        var x = e.pageX - scrollNav.offsetLeft;
        var walk = (x - startX) * 2;
        scrollNav.scrollLeft = scrollLeft - walk;
      });
    }

    // ========================================
    // Mobile scroll mode - drag
    // ========================================

    var mobileScroll = section.querySelector('.bot-ptabs__nav-scroll');
    if (mobileScroll) {
      var mIsDown = false;
      var mStartX = 0;
      var mScrollLeft = 0;

      mobileScroll.addEventListener('mousedown', function(e) {
        mIsDown = true;
        mStartX = e.pageX - mobileScroll.offsetLeft;
        mScrollLeft = mobileScroll.scrollLeft;
      });

      mobileScroll.addEventListener('mouseleave', function() { mIsDown = false; });
      mobileScroll.addEventListener('mouseup', function() { mIsDown = false; });

      mobileScroll.addEventListener('mousemove', function(e) {
        if (!mIsDown) return;
        e.preventDefault();
        var x = e.pageX - mobileScroll.offsetLeft;
        var walk = (x - mStartX) * 2;
        mobileScroll.scrollLeft = mScrollLeft - walk;
      });
    }

    // ========================================
    // Nav scroll arrows (underline / scroll modes)
    // ========================================

    var navArrows = section.querySelectorAll('[data-nav-scroll]');
    if (navArrows.length) {
      var scrollableNav = section.querySelector('.bot-ptabs__nav--underline, .bot-ptabs__nav--scroll');
      if (scrollableNav) {
        navArrows.forEach(function(arrow) {
          arrow.addEventListener('click', function() {
            var dir = arrow.getAttribute('data-nav-scroll');
            var amount = scrollableNav.offsetWidth * 0.6;
            scrollableNav.scrollBy({
              left: dir === 'next' ? amount : -amount,
              behavior: 'smooth'
            });
          });
        });
      }
    }

    // ========================================
    // Window resize
    // ========================================

    var resizeTimer;
    window.addEventListener('resize', function() {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function() {
        // Update underline
        if (underlineIndicator) {
          updateUnderline(currentIndex);
        }
        // Update sliders
        Object.keys(sliders).forEach(function(key) {
          if (sliders[key]) sliders[key].update();
        });
      }, 150);
    });

    // ========================================
    // Initial setup
    // ========================================

    // Init underline on load
    if (underlineIndicator && desktopTabs.length > 0) {
      requestAnimationFrame(function() {
        updateUnderline(0);
      });
    }

    // Init slider for first panel
    if (contentMode === 'slider') {
      initSliderForPanel(0);
    }

    // ========================================
    // Shopify Theme Editor: block:select
    // ========================================

    if (window.Shopify && window.Shopify.designMode) {
      document.addEventListener('shopify:block:select', function(e) {
        var blockEl = e.target;
        if (!section.contains(blockEl)) return;

        // Find the panel that contains this block
        var panel = blockEl.closest('.bot-ptabs__panel');
        if (panel) {
          var idx = parseInt(panel.dataset.panel, 10);
          switchTab(idx);
          return;
        }

        // Or find the tab button
        var tabBtn = blockEl.closest('.bot-ptabs__tab');
        if (tabBtn) {
          var tabIdx = parseInt(tabBtn.dataset.tabIndex, 10);
          switchTab(tabIdx);
        }
      });
    }
  }

  // ========================================
  // Initialize
  // ========================================

  function initAll() {
    var sections = document.querySelectorAll('.bot-ptabs[data-section-id]');
    sections.forEach(initPremiumTabs);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Shopify Theme Editor: section load/reload
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', function(e) {
      var section = e.target.querySelector('.bot-ptabs[data-section-id]');
      initPremiumTabs(section);
    });
  }

  // Label disabled buttons as "Agotado"
  function labelDisabledButtons() {
    document.querySelectorAll('.bot-card__quick-btn[disabled]:not(.bot-card__quick-btn--soldout)').forEach(function(btn) {
      btn.textContent = 'Agotado';
    });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', labelDisabledButtons);
  } else {
    labelDisabledButtons();
  }
})();
